import { TIMING_MS } from '@/constants/timing';
import React, { useEffect, useMemo, useState } from 'react';
import { Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  useGetCashFlowWizardEligibilityQuery,
  WizardEligibilityResponse,
} from '@/store/transactionsApi';
import BannerList, { BannerItem } from '@/components/common/BannerList';
import UncategorizedTransactionWizard from './wizards/UncategorizedTransactionWizard';
import PositiveTransactionWizard from './wizards/PositiveTransactionWizard';
import DuplicateTransactionWizard from './wizards/DuplicateTransactionWizard';

interface WizardEligibilityProps {
  startDate: string;
  endDate: string;
  investorId?: number;
}

type WizardScenario = 'positive' | 'uncategorized' | 'duplicates';

type WizardIssue = {
  id: WizardScenario;
  isActive: boolean;
  line: string; // one-line summary for combined banner
  singleTitle: string; // title for single banner
  singleBody: string; // body for single banner
};

export const WizardEligibility: React.FC<WizardEligibilityProps> = ({
  startDate,
  endDate,
  investorId,
}) => {
  const { t } = useTranslation();
  const {
    data: wizardEligibilityRaw,
    currentData: wizardEligibilityCurrentRaw,
    isLoading,
    isFetching,
    error,
  } = useGetCashFlowWizardEligibilityQuery({
    startDate,
    endDate,
    ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
  });

  // The RTK `useQuery` return type collapses to `any` under the type-aware lint
  // service, so its data reads as unsafe even though the endpoint types it
  // correctly. Re-assert the consumed data to its real shape (same pattern as
  // AddTransactionModal).
  const wizardEligibility = wizardEligibilityRaw as
    | WizardEligibilityResponse
    | undefined;
  const wizardEligibilityCurrentData = wizardEligibilityCurrentRaw as
    | WizardEligibilityResponse
    | undefined;

  const theme = useTheme();
  const [eligibilityRefreshing, setEligibilityRefreshing] = useState(false);

  // State for wizard modals
  const [uncategorizedWizardOpen, setUncategorizedWizardOpen] = useState(false);
  const [positiveWizardOpen, setPositiveWizardOpen] = useState(false);
  const [duplicatesWizardOpen, setDuplicatesWizardOpen] = useState(false);

  // State for wizard chaining
  const [wizardQueue, setWizardQueue] = useState<WizardScenario[]>([]);
  const [currentWizardIndex, setCurrentWizardIndex] = useState(0);
  const [isChaining, setIsChaining] = useState(false);

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL LOGIC
  useEffect(() => {
    if (!isFetching) {
      setEligibilityRefreshing(false);
    }
  }, [isFetching]);

  // Create issues array - moved up to avoid hook dependency issues
  const issues: WizardIssue[] = wizardEligibility
    ? [
        // { comented out because the ticket is onhoding for this functionality until it is defined if needed due to overlaping functionality
        //   id: 'positive',
        //   isActive: wizardEligibility.ShowPositiveTransactionsWizard,
        //   line: t(
        //     'cashFlowWizard.combined.positive', { count: wizardEligibility.PositiveUncategorizedCount }
        //   ),
        //   singleTitle: t('cashFlowWizard.positive.title', 'Positive Transactions in Spending'),
        //   singleBody: t(
        //     'cashFlowWizard.positive.description', { count: wizardEligibility.PositiveUncategorizedCount }
        //   ),
        // },
        {
          id: 'uncategorized',
          isActive: wizardEligibility.ShowUncategorizedTransactionsWizard,
          line: t('cashFlowWizard.combined.uncategorized', {
            count: wizardEligibility.UncategorizedCount,
          }),
          singleTitle: t(
            'cashFlowWizard.uncategorized.title',
            'Uncategorized Transactions',
          ),
          singleBody: t('cashFlowWizard.uncategorized.description', {
            count: wizardEligibility.UncategorizedCount,
          }),
        },
        {
          id: 'duplicates',
          isActive: wizardEligibility.ShowDuplicateTransactionsWizard,
          line: t('cashFlowWizard.combined.duplicates', {
            count: wizardEligibility.DuplicatesCount,
          }),
          singleTitle: t(
            'cashFlowWizard.duplicate.title',
            'Duplicate Transactions',
          ),
          singleBody: t('cashFlowWizard.duplicate.description', {
            count: wizardEligibility.DuplicatesCount,
          }),
        },
      ]
    : [];

  const activeIssues = issues.filter((i) => i.isActive);
  const activeIssueIds = activeIssues.map((issue) => issue.id).join(',');
  const wizardQueueCandidates = useMemo<WizardScenario[]>(
    () =>
      activeIssueIds
        .split(',')
        .filter(
          (issueId): issueId is WizardScenario =>
            issueId === 'positive' ||
            issueId === 'uncategorized' ||
            issueId === 'duplicates',
        ),
    [activeIssueIds],
  );

  // Initialize wizard queue when active issues change - MOVED UP TO BE WITH OTHER HOOKS
  useEffect(() => {
    if (!isChaining && wizardQueueCandidates.length > 0) {
      setWizardQueue(wizardQueueCandidates);
      setCurrentWizardIndex(0);
      setIsChaining(false);
    }
  }, [isChaining, wizardQueueCandidates]);

  // NOW we can do conditional logic and early returns
  // Return null for loading, error, or no data
  // Hide only while there's no data to show for the CURRENT args (first load or
  // a date/arg change), not during a background refetch. Using `currentData`
  // (which persists across same-arg refetches) instead of `isFetching` stops the
  // banner blinking out when an external cache invalidation — e.g. a bulk upload
  // finalizing transactions — refetches eligibility. `eligibilityRefreshing`
  // still keeps the banner up during a wizard-initiated refresh.
  if (
    isLoading ||
    (!wizardEligibilityCurrentData && !eligibilityRefreshing) ||
    error ||
    !wizardEligibility
  ) {
    return null;
  }

  const hasWizards =
    wizardEligibility.ShowPositiveTransactionsWizard ||
    wizardEligibility.ShowUncategorizedTransactionsWizard ||
    wizardEligibility.ShowDuplicateTransactionsWizard;

  if (!hasWizards) {
    return null;
  }

  if (activeIssues.length === 0) return null;

  const bannerItems: BannerItem[] = [];

  const openWizard = (scenario: WizardScenario | 'combined') => {
    // Reset chaining state when manually opening a wizard
    setIsChaining(false);
    setCurrentWizardIndex(0);

    if (scenario === 'uncategorized') {
      setUncategorizedWizardOpen(true);
    } else if (scenario === 'positive') {
      setPositiveWizardOpen(true);
    } else if (scenario === 'duplicates') {
      setDuplicatesWizardOpen(true);
    } else {
      // For combined, start the chaining flow with the first wizard in queue
      if (wizardQueue.length > 0) {
        setIsChaining(true);
        setCurrentWizardIndex(0);
        const firstWizard = wizardQueue[0];
        if (firstWizard === 'uncategorized') {
          setUncategorizedWizardOpen(true);
        } else if (firstWizard === 'positive') {
          setPositiveWizardOpen(true);
        } else {
          // 'duplicates' — the only remaining WizardScenario
          setDuplicatesWizardOpen(true);
        }
      }
    }
  };

  const handleWizardComplete = (completedWizard: WizardScenario) => {
    // Close the completed wizard
    if (completedWizard === 'uncategorized') {
      setUncategorizedWizardOpen(false);
    } else if (completedWizard === 'positive') {
      setPositiveWizardOpen(false);
    } else {
      // 'duplicates' — the only remaining WizardScenario
      setDuplicatesWizardOpen(false);
    }

    // If we're chaining and there's a next wizard, open it
    if (isChaining && currentWizardIndex < wizardQueue.length - 1) {
      const nextIndex = currentWizardIndex + 1;
      setCurrentWizardIndex(nextIndex);
      const nextWizard = wizardQueue[nextIndex];

      // Small delay to make transition smoother
      setTimeout(() => {
        if (nextWizard === 'uncategorized') {
          setUncategorizedWizardOpen(true);
        } else if (nextWizard === 'positive') {
          setPositiveWizardOpen(true);
        } else {
          // 'duplicates' — the only remaining WizardScenario
          setDuplicatesWizardOpen(true);
        }
      }, TIMING_MS.WIZARD_CHAIN_DELAY);
    } else {
      // End of chain or not chaining
      setIsChaining(false);
      setCurrentWizardIndex(0);
    }
  };

  const handleWizardClose = (wizard: WizardScenario) => {
    // Manual close - stop chaining
    setIsChaining(false);
    setCurrentWizardIndex(0);

    switch (wizard) {
      case 'uncategorized': {
        setUncategorizedWizardOpen(false);
        break;
      }
      case 'positive': {
        setPositiveWizardOpen(false);
        break;
      }
      case 'duplicates': {
        setDuplicatesWizardOpen(false);
        break;
      }
      default: {
        console.error('Unknown WizardScenario: ', wizard);
      }
    }
  };

  // Combined banner for 2 or 3
  if (activeIssues.length >= 2) {
    bannerItems.push({
      id: 'combined-wizard',
      content: (
        <Typography
          component='div'
          sx={{ padding: '0.5rem' }}
        >
          <Typography
            variant='subtitle2'
            sx={{ fontWeight: 600, mb: 0.5 }}
          >
            {t(
              'cashFlowWizard.combined.title',
              `We've found some items that need your attention:`,
            )}
          </Typography>

          {activeIssues.map((issue) => (
            <Typography
              key={issue.id}
              variant='body2'
              sx={{ color: 'text.secondary' }}
            >
              {issue.line}
            </Typography>
          ))}
        </Typography>
      ),
      actionLabel: t('cashFlowWizard.combined.action', 'Review transactions'),
      onDismiss: () => {
        openWizard('combined');
      }, // Start the chaining flow with all available wizards
    });
  } else {
    const issue = activeIssues[0];
    bannerItems.push({
      id: `${issue.id}-wizard`,
      content: (
        <Typography
          component='div'
          sx={{ padding: '0.5rem' }}
        >
          <Typography
            variant='subtitle2'
            sx={{ fontWeight: 600, mb: 0.5 }}
          >
            {issue.singleTitle}
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: 'text.secondary' }}
          >
            {issue.singleBody}
          </Typography>
        </Typography>
      ),
      actionLabel: t('cashFlowWizard.single.action', 'Review transactions'),
      onDismiss: () => {
        openWizard(issue.id);
      },
    });
  }

  return (
    <>
      <BannerList
        items={bannerItems}
        containerSx={{
          mx: 3, // 24px to match main content padding
          mb: 2,
          mt: 2,
          [theme.breakpoints.down(800)]: {
            mx: 0, // 0px margin on mobile to match main content
          },
        }}
        actionButtonProps={{
          disableRipple: true,
        }}
      />

      {/* Wizard Modals */}
      <UncategorizedTransactionWizard
        open={uncategorizedWizardOpen}
        onClose={() => {
          handleWizardClose('uncategorized');
        }}
        onComplete={() => {
          handleWizardComplete('uncategorized');
        }}
        startDate={startDate}
        endDate={endDate}
        investorId={investorId}
        onRefreshStart={() => {
          setEligibilityRefreshing(true);
        }}
        isChaining={isChaining}
        chainProgress={
          isChaining
            ? { current: currentWizardIndex + 1, total: wizardQueue.length }
            : undefined
        }
      />
      <DuplicateTransactionWizard
        open={duplicatesWizardOpen}
        onClose={() => {
          handleWizardClose('duplicates');
        }}
        onComplete={() => {
          handleWizardComplete('duplicates');
        }}
        startDate={startDate}
        endDate={endDate}
        investorId={investorId}
        onRefreshStart={() => {
          setEligibilityRefreshing(true);
        }}
      />

      <PositiveTransactionWizard
        open={positiveWizardOpen}
        onClose={() => {
          handleWizardClose('positive');
        }}
        onComplete={() => {
          handleWizardComplete('positive');
        }}
        startDate={startDate}
        endDate={endDate}
        investorId={investorId}
        onRefreshStart={() => {
          setEligibilityRefreshing(true);
        }}
        incomeCategoryId={wizardEligibility.IncomeCategoryId ?? undefined}
        transferCategoryId={wizardEligibility.TransferCategoryId ?? undefined}
        isChaining={isChaining}
        chainProgress={
          isChaining
            ? { current: currentWizardIndex + 1, total: wizardQueue.length }
            : undefined
        }
      />
    </>
  );
};
